import React from "react";
import { Button, DatePicker, Input, Tree, TreeDataNode } from "antd";
import Ring from "@/components/ui/Ring";
import PageTitle from "@/components/ui/PageTitle";
import CompanyLogo from "@/components/ui/CompanyLogo";
import SegmentedToggle, { SegmentedToggleValue } from "@/popups/uploadData/components/SegmentedToggle";
import "@/styles/globals.css";
import { log } from "@/utils/logger";
import { DialogMessageType } from "@/types/commands";

export default function UploadData() {
  const [type, setType] = React.useState<SegmentedToggleValue>("create-new");
  const [selectedUnnKey, setSelectedUnnKey] = React.useState<string | null>(null);
  const infoContent = (
    <div className="text-sm text-left space-y-2">
      <p>
        Upload your proprietary data to AlphaScript and use it across the platform.{" "}
        <strong>Follow these steps:</strong>
      </p>
      <ol className="list-decimal ml-6 space-y-1">
        <li>
          <strong>Choose your data type</strong> (e.g., capitalization, financials).
        </li>
        <li>
          <strong>Open the Upload Template</strong> from the Excel Add-in ribbon and select the template for your data.
        </li>
        <li>
          <strong>Fill out the template</strong> with your data.
        </li>
        <li>
          <strong>Complete this Upload Data form</strong>, ensuring the matching Excel template is open.
        </li>
      </ol>
    </div>
  );

  const treeData: TreeDataNode[] = [
    {
        title: 'Public Information',
        key: 'public-information',
        children: [
          {
            title: 'UNN: Tech IBD Global',
            key: 'unn-tech-ibd-global',
            isLeaf: true,
          }
        ]
           
    },
    {
        title: 'Project Rocket_2024',
        key: 'project-rocket-2024',
        children: [
          {
            title: 'UNN: Company Data',
            key: 'unn-company-data',
            isLeaf: true,
          }
        ]
    }
];

  const handleTreeSelect = (selectedKeys: React.Key[], info: any) => {
    if (selectedKeys.length === 0) {
      // No selection: keep whatever was previously selected (or none)
      setSelectedUnnKey((prev) => prev);
      return;
    }

    if (selectedKeys.length > 0 && info.node.isLeaf) {
      // Only allow selecting leaf nodes (actual UNNs)
      const selected = selectedKeys[0].toString();
      setSelectedUnnKey(selected);
    } else {
      // Clicked on a parent/group node; keep the previous leaf selection
      setSelectedUnnKey((prev) => prev);
    }
  };

  const handleCancel = () => {
    Office.context.ui.messageParent(JSON.stringify({
      action: DialogMessageType.CLOSE,
    }));
  };

  return (
    <Ring className="p-4 mt-4 flex flex-col gap-8 items-center justify-center text-center">
        <div className="flex items-center justify-between w-full">
           <div className="flex items-center gap-2">
                <PageTitle className="!mb-0 flex items-center gap-2" infoTooltip={infoContent} >Upload Data</PageTitle>
           </div>
            <CompanyLogo domain="morganstanley.com" height={26} />
        </div>
       
        <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-2 items-center">
                <label className="whitespace-nowrap w-[200px] text-start font-bold">Company Search: </label>
                <Input 
                    placeholder="Select Data Type" 
                    defaultValue={"Apple (Nasdaq:AAPL)"}
                    className='w-full border-2 border-black rounded-md' 
                />
            </div>
            <div className="flex gap-2 items-center">
                <label className="whitespace-nowrap w-[200px] text-start font-bold">Type: </label>
                <SegmentedToggle className="w-full" value={type} onChange={setType} />
            </div>
            <div className="flex gap-2 flex-col items-start">
                <label className="whitespace-nowrap text-start font-bold">Select Access Group and Unique Upload Number (UNN) </label>
                <Ring className="w-full  py-4 px-2">
                  <Tree
                    defaultExpandAll
                    selectedKeys={selectedUnnKey ? [selectedUnnKey] : []}
                    onSelect={handleTreeSelect}
                    treeData={treeData}
                    showLine={{ showLeafIcon: false }}
                  />
                </Ring>

                <div className="w-full text-left text-xs text-[var(--color-as-dark-gray)] mt-2">
                  Don&apos;t see your Access Group? Click{" "}
                  <button
                    type="button"
                    className="text-[var(--color-as-dark-blue)] underline font-semibold hover:text-[var(--color-as-orange-primary)] hover:cursor-pointer"
                    onClick={() => {
                      log.info("Access Groups link clicked");
                    }}
                  >
                    here
                  </button>{" "}
                  to create and manage Access Groups.
                </div>
            </div>

            <div className="flex gap-2 items-center">
                <label className="whitespace-nowrap w-[200px] text-start font-bold">Period End Date: </label>
                <DatePicker 
                    placeholder="Select Period End Date" 
                    className='w-full border-2 border-black rounded-md' 
                />
            </div>
            <div className="flex gap-2 items-center justify-end">
              <Button onClick={handleCancel} className=" hover:!bg-[var(--color-as-green-primary)]">Cancel</Button>
              <Button type="primary" onClick={() => (log.info("Upload Data button clicked"))} className="!bg-[var(--color-as-green-primary)] hover:!bg-[var(--color-as-green-primary)]/80">Upload</Button>
            </div>
        </div>
    </Ring>
  );
}

